<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="$emit('close')"></div>

    <section
      class="app-modal__dialog invoice-concept-modal__dialog invoice-concept-modal__dialog--payment-plan"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invoice-concept-modal-plan-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="invoice-concept-modal-plan-title">{{ text.create.ui.conceptModal.paymentPlan.title }}</h3>
          <p>{{ text.create.ui.conceptModal.paymentPlan.subtitle }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.create.ui.conceptModal.closeAriaLabel"
          @click="$emit('close')"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <section v-if="showModeSelector" class="invoice-concept-modal__mode-selector">
        <label class="invoice-concept-modal__mode-option app-modal-toggle">
          <input
            type="checkbox"
            :checked="sourceMode === 'payment-plan'"
            @change="$emit('change-source-mode', 'payment-plan')"
          >
          <span>{{ text.create.ui.conceptModal.modeSelector.paymentPlan }}</span>
        </label>
        <label class="invoice-concept-modal__mode-option app-modal-toggle">
          <input
            type="checkbox"
            :checked="sourceMode === 'manual'"
            @change="$emit('change-source-mode', 'manual')"
          >
          <span>{{ text.create.ui.conceptModal.modeSelector.manual }}</span>
        </label>
      </section>

      <div class="app-modal__content">
        <section class="app-modal__section">
          <div class="invoice-plan-toolbar app-filter-toolbar-row">
            <div class="app-filter-search-wrap">
              <i class="fa fa-search" aria-hidden="true"></i>
              <input
                v-model.trim="search"
                type="search"
                :placeholder="text.create.ui.conceptModal.paymentPlan.toolbar.searchPlaceholder"
              >
            </div>

            <multiselect
              v-model="selectedBusiness"
              :options="businessOptions"
              track-by="value"
              label="label"
              :show-labels="false"
              :searchable="true"
              :allow-empty="false"
              class="invoice-plan-toolbar__business app-filter-inline-select app-filter-select app-select app-select--filters"
              :placeholder="text.create.ui.conceptModal.paymentPlan.toolbar.businessPlaceholder"
            >
              <template #noResult><span>{{ generalText.common.noOption }}</span></template>
              <template #noOptions><span>{{ generalText.common.noOption }}</span></template>
            </multiselect>
          </div>
        </section>

        <section class="app-modal__section invoice-plan-summary-section">
          <div class="invoice-plan-selection-summary">
            <div class="invoice-plan-selection-summary__left">
              <span class="invoice-plan-selection-summary__badge">{{ selectedCount }}</span>
              <span class="invoice-plan-selection-summary__label">{{ text.create.ui.conceptModal.paymentPlan.summary.selected }}</span>
              <span class="invoice-plan-selection-summary__separator">|</span>
              <span class="invoice-plan-selection-summary__label">{{ text.create.ui.conceptModal.paymentPlan.summary.amount }}</span>
              <strong>{{ formatMoney(selectedAmount) }}</strong>
            </div>

            <label class="invoice-plan-selection-summary__merge app-modal-toggle invoice-plan-selection-summary__merge--flat">
              <input v-model="mergeConcepts" type="checkbox">
              <span>{{ text.create.ui.conceptModal.paymentPlan.summary.merge }}</span>
            </label>
          </div>
        </section>

        <section class="app-modal__section invoice-plan-table-section">
          <div class="app-table-wrap">
            <table class="app-data-table invoice-plan-table">
              <thead>
                <tr>
                  <th class="invoice-plan-table__check-col"></th>
                  <th class="invoice-plan-table__date-col">{{ text.create.ui.conceptModal.paymentPlan.table.headers.date }}</th>
                  <th class="invoice-plan-table__concept-col">Concepto</th>
                  <th class="app-text-right invoice-plan-table__amount-col">{{ text.create.ui.conceptModal.paymentPlan.table.headers.amount }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="4" class="app-table-empty">{{ text.create.ui.conceptModal.paymentPlan.table.loading }}</td>
                </tr>

                <tr v-else-if="groupedRows.length === 0">
                  <td colspan="4" class="app-table-empty">{{ text.create.ui.conceptModal.paymentPlan.table.empty }}</td>
                </tr>

                <template v-else>
                  <template v-for="group in groupedRows" :key="group.key">
                    <tr class="invoice-plan-table__group-row">
                      <td colspan="4">
                        <div class="invoice-plan-table__group-head">
                          <strong>{{ group.label }}</strong>
                          <button
                            type="button"
                            class="invoice-plan-table__group-toggle"
                            @click="toggleGroupCollapse(group.key)"
                          >
                            <span>{{ isGroupCollapsed(group.key) ? 'Mostrar' : 'Ocultar' }}</span>
                            <i
                              class="fa"
                              :class="isGroupCollapsed(group.key) ? 'fa-chevron-down' : 'fa-chevron-up'"
                              aria-hidden="true"
                            ></i>
                          </button>
                        </div>
                      </td>
                    </tr>

                    <tr
                      v-for="row in group.rows"
                      v-show="!isGroupCollapsed(group.key)"
                      :key="row.id"
                    >
                      <td>
                        <input
                          type="checkbox"
                          class="invoice-plan-table__row-check"
                          :checked="isSelected(row.id)"
                          @change="toggleRow(row.id)"
                        >
                      </td>
                      <td class="invoice-plan-table__date-cell">{{ formatDate(row.date) }}</td>
                      <td class="invoice-plan-table__concept-cell">{{ row.concept }}</td>
                      <td class="app-text-right app-strong">{{ formatMoney(resolveRowTotal(row)) }}</td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </table>
          </div>
        </section>

        <footer class="app-modal__footer">
          <div class="app-modal__footer-right">
            <button type="button" class="btn btn-light" @click="$emit('close')">{{ generalText.common.cancel }}</button>
            <button type="button" class="btn btn-primary" :disabled="selectedCount <= 0" @click="submitSelection">
              {{ text.create.ui.conceptModal.paymentPlan.actions.add }} ({{ selectedCount }})
            </button>
          </div>
        </footer>
      </div>
    </section>
  </div>
</template>

<script>
import paymentPlansService from '@/services/paymentPlans';
import { useDomainText } from '@/composables/text/useDomainText';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import { formatDateShort } from '@/utils/datePresentation';
import { formatAmountWithCurrency } from '@/utils/numberFormat';

const { catalog: invoiceText } = useDomainText('invoices');
const { catalog: generalText } = useDomainText('general');

export default {
  name: 'InvoiceCreateConceptPaymentPlanMode',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    paymentId: {
      type: Number,
      default: 0,
    },
    existingConcepts: {
      type: Array,
      default: () => [],
    },
    selectedReceiver: {
      type: Object,
      default: null,
    },
    currency: {
      type: String,
      default: 'MXN',
    },
    showModeSelector: {
      type: Boolean,
      default: false,
    },
    sourceMode: {
      type: String,
      default: 'payment-plan',
    },
  },
  emits: ['close', 'submit', 'change-source-mode'],
  data() {
    return {
      text: invoiceText,
      generalText,
      loading: false,
      plans: [],
      loadedContextKey: '',
      search: '',
      selectedBusiness: { value: 'all', label: invoiceText.create.ui.conceptModal.paymentPlan.toolbar.allBusinesses },
      selectedRowIds: [],
      mergeConcepts: false,
      collapsedGroupKeys: {},
    };
  },
  computed: {
    businessOptions() {
      const options = this.plans.reduce((acc, row) => {
        if (!acc.some((item) => String(item.value) === String(row.businessKey))) {
          acc.push({
            value: row.businessKey,
            label: row.businessName,
          });
        }
        return acc;
      }, []);

      return [
        { value: 'all', label: this.text.create.ui.conceptModal.paymentPlan.toolbar.allBusinesses },
        ...options,
      ];
    },
    filteredRows() {
      const business = String(this.selectedBusiness?.value || 'all');
      const query = String(this.search || '').trim().toLowerCase();

      return this.plans.filter((row) => {
        if (business !== 'all' && String(row.businessKey) !== business) {
          return false;
        }

        if (query === '') {
          return true;
        }

        return [row.concept, row.businessName, row.date]
          .some((value) => String(value || '').trim().toLowerCase().includes(query));
      });
    },
    groupedRows() {
      const groups = [];
      const map = {};

      this.filteredRows.forEach((row) => {
        const key = String(row.businessKey || row.businessName || row.id || 'group');
        if (!map[key]) {
          map[key] = {
            key,
            label: row.businessName,
            rows: [],
          };
          groups.push(map[key]);
        }

        map[key].rows.push(row);
      });

      return groups;
    },
    selectedRows() {
      const selectedSet = new Set(this.selectedRowIds.map((value) => Number(value || 0) || 0));
      return this.plans.filter((row) => selectedSet.has(Number(row.id || 0) || 0));
    },
    selectedCount() {
      return this.selectedRows.length;
    },
    selectedAmount() {
      return this.selectedRows.reduce((sum, row) => sum + this.resolveRowTotal(row), 0);
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(visible) {
        if (!visible) {
          this.resetState();
          return;
        }

        const contextKey = this.resolveLoadContextKey();
        if (this.plans.length > 0 && contextKey === this.loadedContextKey) {
          this.applyDefaultSelection();
          return;
        }

        this.loadPlans();
      },
    },
    existingConcepts: {
      deep: true,
      handler() {
        if (!this.show || this.loading || this.plans.length <= 0) {
          return;
        }

        this.applyDefaultSelection();
      },
    },
  },
  methods: {
    resolveLoadContextKey() {
      const paymentId = Number(this.paymentId || 0) || 0;
      const customerId = this.resolveReceiverCustomerId();
      return `${paymentId}:${customerId}`;
    },
    applyDefaultSelection() {
      const validIds = new Set(
        this.plans
          .map((row) => Number(row?.id || 0) || 0)
          .filter((id) => id > 0)
      );
      const snapshot = this.resolveExistingSelectionSnapshot();
      this.selectedRowIds = snapshot.selectedIds.filter((id) => validIds.has(id));
      this.mergeConcepts = snapshot.mergeConcepts && this.selectedRowIds.length > 0;
    },
    resolveConceptPaymentIds(concept = {}) {
      const explicitIds = Array.isArray(concept?.sourcePaymentIds)
        ? concept.sourcePaymentIds
        : [];
      const parsedExplicit = explicitIds
        .map((value) => Number(value || 0) || 0)
        .filter((value) => value > 0);

      if (parsedExplicit.length > 0) {
        return [...new Set(parsedExplicit)];
      }

      const paymentId = Number(concept?.paymentId || concept?.payment_id || 0) || 0;
      return paymentId > 0 ? [paymentId] : [];
    },
    resolveExistingSelectionSnapshot() {
      const concepts = Array.isArray(this.existingConcepts) ? this.existingConcepts : [];
      const selectedSet = new Set();
      let mergeConcepts = false;
      const legacyMergedDescription = String(this.text.create.ui.conceptModal.paymentPlan.summary.mergedDescription || '')
        .trim()
        .toLowerCase();

      concepts.forEach((concept) => {
        const ids = this.resolveConceptPaymentIds(concept);
        ids.forEach((id) => selectedSet.add(id));

        const conceptDescription = String(concept?.description || '').trim().toLowerCase();
        const isLegacyMergedConcept = legacyMergedDescription !== ''
          && conceptDescription !== ''
          && conceptDescription === legacyMergedDescription;

        if (Boolean(concept?.isPaymentPlanMerged) || ids.length > 1 || isLegacyMergedConcept) {
          mergeConcepts = true;
        }
      });

      return {
        selectedIds: [...selectedSet],
        mergeConcepts,
      };
    },
    formatDate(value) {
      return formatDateShort(value);
    },
    formatMoney(amount) {
      const currency = String(this.currency || 'MXN').trim().toUpperCase() || 'MXN';
      return formatAmountWithCurrency(amount, currency);
    },
    resetState() {
      this.search = '';
      this.selectedBusiness = { value: 'all', label: this.text.create.ui.conceptModal.paymentPlan.toolbar.allBusinesses };
      this.selectedRowIds = [];
      this.mergeConcepts = false;
      this.collapsedGroupKeys = {};
    },
    resolveRowTotal(row) {
      const total = Number(row?.total || 0);
      if (Number.isFinite(total) && total > 0) {
        return total;
      }

      const subtotal = Number(row?.subtotal || 0) || 0;
      const transferred = Number(row?.taxes?.transferred_total || 0) || 0;
      const withheld = Number(row?.taxes?.withheld_total || 0) || 0;

      return subtotal + transferred - withheld;
    },
    resolveReceiverCustomerId() {
      return Number(
        this.selectedReceiver?.meta?.customerId
        || this.selectedReceiver?.customerId
        || this.selectedReceiver?.meta?.companyId
        || this.selectedReceiver?.companyId
        || 0
      ) || 0;
    },
    normalizePlanRow(item = {}, index = 0) {
      const id = Number(item?.id || 0) || index + 1;
      const saleId = Number(item?.sale_id || item?.area_id || 0) || 0;
      const companyId = Number(item?.company_id || item?.customer_id || 0) || 0;
      const saleCode = String(item?.sale_code || '').trim();
      const saleName = String(item?.sale_name || '').trim();
      const businessFromSale = saleName !== ''
        ? (saleCode !== '' ? `${saleCode} - ${saleName}` : saleName)
        : '';
      const businessName = String(
        businessFromSale
        || item?.area_name
        || item?.company_name
        || item?.customer_name
        || this.text.create.ui.conceptModal.paymentPlan.table.unknownBusiness
      ).trim() || this.text.create.ui.conceptModal.paymentPlan.table.unknownBusiness;

      return {
        id,
        saleId,
        companyId,
        businessKey: saleId > 0 ? `sale-${saleId}` : `company-${companyId || id}`,
        businessName,
        concept: String(item?.concept || '').trim() || this.text.create.prefill.defaultConceptDescription,
        date: String(item?.date || '').trim(),
        subtotal: Number(item?.subtotal || item?.amount || 0) || 0,
        total: Number(item?.total || 0) || 0,
        taxes: item?.taxes && typeof item.taxes === 'object'
          ? item.taxes
          : { transferred_total: 0, withheld_total: 0, items: [] },
        taxId: Number(item?.tax_id || 0) || 0,
        taxGroupId: Number(item?.tax_group_id || 0) || 0,
        productId: Number(item?.product_id || 0) || 0,
        satCode: String(item?.sat_code || '').trim(),
        unitCode: String(item?.unit_code || '').trim(),
        unitName: String(item?.unit_name || '').trim(),
      };
    },
    async loadPlans() {
      if (this.loading) {
        return;
      }

      this.loading = true;
      try {
        const contextKey = this.resolveLoadContextKey();
        let anchor = {};
        const normalizedPaymentId = Number(this.paymentId || 0) || 0;
        if (normalizedPaymentId > 0) {
          const detailResponse = await paymentPlansService.show(normalizedPaymentId);
          const detailPayload = extractPayload(detailResponse);
          anchor = detailPayload && typeof detailPayload === 'object' && detailPayload.data && typeof detailPayload.data === 'object'
            ? detailPayload.data
            : detailPayload;
        }

        const customerId = Number(anchor?.company_id || anchor?.customer_id || this.resolveReceiverCustomerId() || 0) || 0;

        const params = {
          billed: 0,
          voucher_type: 'cfdi',
          include_business: 1,
        };

        if (customerId > 0) {
          params.customer_id = customerId;
        }

        const response = await paymentPlansService.index(params);
        const collection = extractCollection(response);
        const rows = (Array.isArray(collection) ? collection : []).map((item, index) => this.normalizePlanRow(item, index));

        if (normalizedPaymentId > 0 && anchor && typeof anchor === 'object') {
          const anchorRow = this.normalizePlanRow(anchor, rows.length);
          const anchorId = Number(anchorRow?.id || 0) || 0;
          const alreadyIncluded = rows.some((row) => Number(row?.id || 0) === anchorId);

          if (anchorId > 0 && !alreadyIncluded) {
            rows.unshift(anchorRow);
          }
        }

        this.plans = rows;
        this.applyDefaultSelection();

        this.loadedContextKey = contextKey;
      } catch (error) {
        this.plans = [];
        this.selectedRowIds = [];
        this.loadedContextKey = '';
      } finally {
        this.loading = false;
      }
    },
    isSelected(rowId) {
      const normalized = Number(rowId || 0) || 0;
      return this.selectedRowIds.some((id) => Number(id || 0) === normalized);
    },
    toggleRow(rowId) {
      const normalized = Number(rowId || 0) || 0;
      if (normalized <= 0) {
        return;
      }

      if (this.isSelected(normalized)) {
        this.selectedRowIds = this.selectedRowIds.filter((id) => Number(id || 0) !== normalized);
        return;
      }

      this.selectedRowIds = [...this.selectedRowIds, normalized];
    },
    isGroupCollapsed(groupKey) {
      return Boolean(this.collapsedGroupKeys?.[String(groupKey || '')]);
    },
    toggleGroupCollapse(groupKey) {
      const normalizedKey = String(groupKey || '');
      if (normalizedKey === '') {
        return;
      }

      this.collapsedGroupKeys = {
        ...this.collapsedGroupKeys,
        [normalizedKey]: !this.isGroupCollapsed(normalizedKey),
      };
    },
    resolveTaxLabel(row) {
      const item = Array.isArray(row?.taxes?.items) ? row.taxes.items[0] : null;
      if (item && typeof item === 'object') {
        const name = String(item?.name || item?.code || this.generalText.financial.taxes.tax).trim();
        const rawRate = Number(item?.rate || item?.percentage || item?.percent || 0) || 0;
        const rate = rawRate > 0 && rawRate <= 1 ? rawRate * 100 : rawRate;
        if (rate > 0) {
          const normalizedRate = String(rate).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
          return `${name} ${normalizedRate}%`;
        }

        return name;
      }

      return this.generalText.financial.taxes.defaultTransferredLabel;
    },
    buildConceptFromRow(row) {
      const unitCode = String(row?.unitCode || this.text.create.prefill.defaultUnitCode).trim() || this.text.create.prefill.defaultUnitCode;
      const unitName = String(row?.unitName || this.text.create.prefill.defaultUnitName).trim() || this.text.create.prefill.defaultUnitName;
      const taxes = row?.taxes && typeof row.taxes === 'object'
        ? row.taxes
        : { transferred_total: 0, withheld_total: 0, items: [] };
      const transferred = Number(taxes?.transferred_total || 0) || 0;
      const withheld = Number(taxes?.withheld_total || 0) || 0;

      return {
        paymentId: Number(row?.id || 0) || 0,
        saleId: Number(row?.saleId || 0) || 0,
        serviceId: Number(row?.productId || 0) || 0,
        productId: Number(row?.productId || 0) || 0,
        qty: 1,
        unit: `${unitCode} - ${unitName}`,
        unitValue: unitCode,
        unitCode,
        unitId: 0,
        code: String(row?.satCode || this.text.create.prefill.defaultSatCode).trim() || this.text.create.prefill.defaultSatCode,
        description: String(row?.concept || '').trim() || this.text.create.prefill.defaultConceptDescription,
        unitPrice: Number(row?.subtotal || 0) || 0,
        taxLabel: this.resolveTaxLabel(row),
        taxAmount: transferred - withheld,
        taxTransferredTotal: transferred,
        taxWithheldTotal: withheld,
        taxId: Number(row?.taxId || 0) || 0,
        taxGroupId: Number(row?.taxGroupId || 0) || 0,
        sourceMode: 'payment-plan',
        sourcePaymentIds: [Number(row?.id || 0) || 0].filter((value) => value > 0),
        isPaymentPlanMerged: false,
      };
    },
    resolveAnchorRow(rows = []) {
      const normalizedRows = Array.isArray(rows) ? rows : [];
      const anchorPaymentId = Number(this.paymentId || 0) || 0;

      if (anchorPaymentId > 0) {
        const selectedMatch = normalizedRows.find((row) => Number(row?.id || 0) === anchorPaymentId);
        if (selectedMatch) {
          return selectedMatch;
        }

        const catalogMatch = this.plans.find((row) => Number(row?.id || 0) === anchorPaymentId);
        if (catalogMatch) {
          return catalogMatch;
        }
      }

      return normalizedRows[0] || null;
    },
    resolveAnchorTaxTotals(anchorRow, amount) {
      const baseAmount = Number(amount || 0) || 0;
      const anchorSubtotal = Number(anchorRow?.subtotal || 0) || 0;
      const anchorTransferred = Number(anchorRow?.taxes?.transferred_total || 0) || 0;
      const anchorWithheld = Number(anchorRow?.taxes?.withheld_total || 0) || 0;

      if (baseAmount <= 0 || anchorSubtotal <= 0) {
        return {
          transferred: 0,
          withheld: 0,
        };
      }

      const transferredRate = anchorTransferred / anchorSubtotal;
      const withheldRate = anchorWithheld / anchorSubtotal;

      return {
        transferred: Math.round((baseAmount * transferredRate) * 100) / 100,
        withheld: Math.round((baseAmount * withheldRate) * 100) / 100,
      };
    },
    buildMergedConcept(rows = []) {
      const normalizedRows = Array.isArray(rows) ? rows : [];
      const amount = normalizedRows.reduce((sum, row) => sum + (Number(row?.subtotal || 0) || 0), 0);
      const selectedPaymentIds = [...new Set(
        normalizedRows
          .map((row) => Number(row?.id || 0) || 0)
          .filter((value) => value > 0)
      )];
      const anchorRow = this.resolveAnchorRow(normalizedRows);
      const anchorPaymentId = Number(anchorRow?.id || 0) || 0;
      const anchorSaleId = Number(anchorRow?.saleId || 0) || 0;
      const anchorTaxId = Number(anchorRow?.taxId || 0) || 0;
      const anchorTaxGroupId = Number(anchorRow?.taxGroupId || 0) || 0;
      const anchorTaxLabel = this.resolveTaxLabel(anchorRow);
      const { transferred, withheld } = this.resolveAnchorTaxTotals(anchorRow, amount);
      const unitCode = this.text.create.prefill.defaultUnitCode;
      const unitName = this.text.create.prefill.defaultUnitName;
      const mergedDescription = String(anchorRow?.concept || '').trim()
        || this.text.create.ui.conceptModal.paymentPlan.summary.mergedDescription;

      return {
        paymentId: anchorPaymentId,
        saleId: anchorSaleId,
        serviceId: 0,
        productId: 0,
        qty: 1,
        unit: `${unitCode} - ${unitName}`,
        unitValue: unitCode,
        unitCode,
        unitId: 0,
        code: this.text.create.prefill.defaultSatCode,
        description: mergedDescription,
        unitPrice: amount,
        taxLabel: anchorTaxLabel,
        taxAmount: transferred - withheld,
        taxTransferredTotal: transferred,
        taxWithheldTotal: withheld,
        taxId: anchorTaxId,
        taxGroupId: anchorTaxGroupId,
        sourceMode: 'payment-plan',
        sourcePaymentIds: selectedPaymentIds,
        isPaymentPlanMerged: true,
      };
    },
    submitSelection() {
      if (this.selectedRows.length <= 0) {
        return;
      }

      const concepts = this.mergeConcepts
        ? [this.buildMergedConcept(this.selectedRows)]
        : this.selectedRows.map((row) => this.buildConceptFromRow(row));

      this.$emit('submit', {
        sourceMode: 'payment-plan',
        mergeConcepts: this.mergeConcepts,
        selectedPaymentIds: this.selectedRows
          .map((row) => Number(row?.id || 0) || 0)
          .filter((value) => value > 0),
        concepts,
      });
    },
  },
};
</script>

<style scoped>
.invoice-concept-modal__mode-selector {
  display: flex;
  gap: 18px;
  padding: 14px 22px;
  border-bottom: 1px solid #e8edf6;
}

.invoice-concept-modal__mode-option {
  width: auto;
  min-width: 210px;
  margin: 0;
}

.invoice-plan-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
  gap: 12px;
}

.invoice-plan-toolbar__business {
  --app-filter-inline-width: 100%;
  width: 100%;
  min-width: 0;
  margin: 0;
}

.invoice-plan-selection-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(153, 152, 153, 0.22);
  border-radius: var(--orderly-radius-md);
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(0, 98, 163, 0.06);
}

.invoice-plan-summary-section {
  padding: 0 24px !important;
}

.invoice-plan-selection-summary__left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.invoice-plan-selection-summary__badge {
  min-width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #0070cd;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.invoice-plan-selection-summary__label {
  color: #6b7280;
  font-size: 13px;
}

.invoice-plan-selection-summary__separator {
  color: #999899;
}

.invoice-plan-selection-summary__merge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

.invoice-plan-selection-summary__merge--flat {
  width: auto;
  min-height: 0;
  padding: 0;
  border: 0;
  background: transparent;
}

.invoice-plan-selection-summary__merge--flat:hover {
  border: 0;
  background: transparent;
}

.invoice-plan-table__row-check {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  margin: 0;
  border: 1px solid rgba(153, 152, 153, 0.9);
  border-radius: 5px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.2s ease;
}

.invoice-plan-table__row-check::after {
  content: '';
  width: 5px;
  height: 9px;
  border: solid transparent;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  margin-top: -1px;
}

.invoice-plan-table__row-check:checked {
  border-color: #0070cd;
  background: linear-gradient(135deg, #0070cd, #005eb1);
  box-shadow: 0 2px 6px rgba(0, 112, 205, 0.35);
}

.invoice-plan-table__row-check:checked::after {
  border-color: #ffffff;
}

.invoice-plan-table__check-col {
  width: 42px;
}

.invoice-plan-table__date-col {
  width: 12%;
}

.invoice-plan-table__amount-col {
  width: 22%;
}

.invoice-plan-table__concept-col {
  width: auto;
}

.invoice-plan-table {
  min-width: 0;
  table-layout: fixed;
}

.invoice-plan-table th,
.invoice-plan-table td {
  word-break: break-word;
}

.invoice-plan-table__concept-cell {
  max-width: 0;
}

.invoice-plan-table__date-cell {
  white-space: nowrap;
}

.invoice-plan-table__group-row td {
  font-size: 12px;
  font-weight: 700;
  color: #494949;
  background: rgba(0, 112, 205, 0.06);
}

.invoice-plan-table__group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.invoice-plan-table__group-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: transparent;
  color: #495057;
  font-size: 12px;
  font-weight: 700;
  padding: 0;
}

.invoice-concept-modal__dialog--payment-plan {
  width: min(980px, calc(100vw - 24px));
  max-height: calc(100vh - 24px);
  display: flex;
  flex-direction: column;
}

.invoice-concept-modal__dialog--payment-plan .app-modal__content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.invoice-concept-modal__dialog--payment-plan .invoice-plan-table-section {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.invoice-concept-modal__dialog--payment-plan .invoice-plan-table-section .app-table-wrap {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.invoice-concept-modal__dialog--payment-plan .app-modal__footer {
  margin-top: auto;
  flex-shrink: 0;
}

@media (max-width: 900px) {
  .invoice-concept-modal__mode-selector {
    flex-direction: column;
    gap: 10px;
  }

  .invoice-plan-toolbar {
    grid-template-columns: 1fr;
  }

  .invoice-plan-selection-summary {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
